/**
 * Modular Chat Window jQuery Plugin
 */

import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options';
import Keyboard from 'styleguide/modules/keyboard'; // Import the Keyboard module for trap focus

// Plugin name and default options
export const PLUGIN_NAME = 'chatWindow';
export const DEFAULTS = {
  chatButton: '.js-chat-window',
  chatWindowTarget: '.js-chat-window-target',
  chatWindowClose: '.js-chat-window-close',
  feedbackButton: '.js-switch-text-status',
  conversationContainer: '#js-chat-window-conversation',
  feedbackContainer: '#js-switch-text-target',
  feedbackButtonContainer: '#js-feedback-button-container',
  feedbackMessageContainer: '#js-submit-feedback-container',
  pendingContainer: undefined, // JQuery object set in logic
  responseContainer: undefined, // JQuery object set in logic
  suggestionsContainer: '#js-chat-window-suggestions',
  windowHeader: '.ai-chat-window__header', // Chat window header selector for a11y focus
  loadingText: 'Thinking',
  activeClass: 'is-active',
  hideClass: 'u-hide',
  messageClass: 'message',
  progressClass: 'ai-chat-window__content-progress',
  loadingIntervalId: null, // Will be set when animation is shown
  loadingDuration: 1500,
  loadingInterval: 500,
  maxDots: 3,
  maxSuggestions: 5,
  // Function references for keyboard focus management
  trapFocusFunc: Keyboard && Keyboard.trapFocus ? Keyboard.trapFocus.bind(Keyboard) : null,
  releaseFocusFunc: Keyboard && Keyboard.releaseFocus ? Keyboard.releaseFocus.bind(Keyboard) : null 
};

/**
 * Sets focus to the chat window header for accessibility and traps focus inside the window
 * @param {Object} options - Options containing the window header selector
 */
function focusWindowHeader(options) {
  // Find the window header
  const $header = $(options.windowHeader);
  
  // If header exists, set focus to it
  if ($header.length) {
    // Set tabindex if it doesn't have one already
    if (!$header.attr('tabindex')) {
      $header.attr('tabindex', '-1');
    }
    
    // Small delay to ensure DOM is ready and transitions are complete
    setTimeout(() => {
      $header.focus();
      
      if (options.chatWindowTarget && options.trapFocusFunc) {
        // Use jQuery selector instead of vanilla DOM
        const $chatWindow = $(options.chatWindowTarget);
        if ($chatWindow.length) {
          options.trapFocusFunc($chatWindow);
        } else {
          console.warn('Chat window element not found:', options.chatWindowTarget);
        }
      }
    }, 100);
  }
}

/**
 * Opens the chat window and shows loading animation
 */
function openChatWindow($trigger, options) {
  const buttonText = $trigger.text();

  // Store the trigger element for focus return when closing
  const $chatWindow = $(options.chatWindowTarget);
  $chatWindow.data('triggerElement', $trigger);

  // Hide & reset feedback element
  resetFeedback(options);

  // Hide suggestions based on what was asked previously, also display next one if any
  resetSuggestions($trigger, options);
  
  // Open chat window
  $(options.chatWindowTarget).addClass(options.activeClass);
  
  // Append question to window
  const $questionContainer = $(`<p class='ai-chat-window__content-question'></p>`);
  $questionContainer.append(`<div class='${options.messageClass}'>${buttonText}</div>`);
  $(options.conversationContainer).append($questionContainer);

  // Append loading container
  options.pendingContainer = $(`<div></div>`);
  $(options.conversationContainer).append(options.pendingContainer);

  // Append response and suggestions if any
  options.responseContainer = $(`<div class='${options.hideClass}'></div>`);
  $(options.conversationContainer).append(options.responseContainer);
  if ($trigger.data('answer')) {
    options.responseContainer.append(`<p>${$trigger.data('answer')}</p>`);
  }

  if ($trigger.data('recommendationsUrl')) {
    const $recommendationsContainer = $(`<div></div>`);
    options.responseContainer.append($recommendationsContainer);
    $.get($trigger.data('recommendationsUrl'), (data) => {
      $recommendationsContainer.html(data);
    });
  }

  const $feedbackButton = $(options.feedbackButton);
  $feedbackButton.data('question', buttonText.trim());
  $feedbackButton.data('answer', $trigger.data('answer'));
  $feedbackButton.data('position', $trigger.data('rank'));

  // Focus on the window header for accessibility and trap focus
  focusWindowHeader(options);
  
  // Show loading animation
  showLoadingAnimation(options);
  stopLoadingAnimation(options);
}

/**
 * Displays loading animation with dots
 */
function showLoadingAnimation(options) {
  // Create loading element
  const $loadingMessage = $(`<div class='${options.progressClass}'>${options.loadingText}</div>`);
  options.pendingContainer.append($loadingMessage);
  
  // Animate dots
  let dots = 0;
  options.loadingIntervalId = setInterval(() => {
    dots = (dots + 1) > options.maxDots ? 1 : dots + 1;
    $loadingMessage.text(`${options.loadingText}${'.'.repeat(dots)}`);
  }, options.loadingInterval);
}

function stopLoadingAnimation(options) {
  // Show response after timeout
  setTimeout(() => {
    clearInterval(options.loadingIntervalId);
    options.pendingContainer.remove();
    options.responseContainer.removeClass(options.hideClass);
    $(options.suggestionsContainer).show();
    $(options.feedbackContainer).show();
    options.pendingContainer = undefined;
    options.responseContainer = undefined;
  }, options.loadingDuration);
}

/**
 * Closes the chat window
 */
function closeChatWindow(options) {
  $(options.chatWindowTarget).removeClass(options.activeClass);

  // Get the stored trigger element
  const $chatWindow = $(options.chatWindowTarget);

  const $triggerElement = $chatWindow.data('triggerElement');
  
  // Release focus trap from the element that had it trapped
  if (options.releaseFocusFunc && typeof options.releaseFocusFunc === 'function') {
    if ($chatWindow.length) {
      options.releaseFocusFunc($chatWindow);
    }
  }
  // Return focus to the trigger element

  if ($triggerElement && $triggerElement.length) {
    // Small delay to ensure the chat window is fully closed
    setTimeout(() => {
      $triggerElement.focus();
    }, 50);
  }
  
  // Clean up stored data
  $chatWindow.removeData('triggerElement');

  // Clear all responses and contents
  $(options.conversationContainer).empty();
  $(`${options.suggestionsContainer} ${options.chatButton}`).removeClass(options.hideClass).removeAttr('data-clicked');
}

function handleFeedbackButton($trigger, options) {
  $trigger.attr('disabled', 'true');
  const $feedbackContainer = $(options.feedbackContainer);
  const data = {
    code: $feedbackContainer.data('code'),
    question: $trigger.data('question'),
    answer: $trigger.data('answer'),
    position: $trigger.data('position'),
    feedback: $trigger.data('feedback') === 1 ? 'Helpful' : 'Not Helpful',
  };
  $.ajax({
    url: $feedbackContainer.data('url'),
    method: 'POST',
    contentType: 'application/json',
    data: JSON.stringify(data),
  });
  setTimeout(function() {
    $feedbackContainer.fadeOut(500, function() {
      $trigger.removeAttr('disabled');
      $(options.feedbackButtonContainer).addClass('u-hide');
      $(options.feedbackMessageContainer).removeClass('u-hide');
      $feedbackContainer.fadeIn(500);
    });
  }, 500);
}

function resetFeedback(options) {
  $(options.feedbackContainer).hide();
  $(options.feedbackButtonContainer).removeClass('u-hide');
  $(options.feedbackMessageContainer).addClass('u-hide');
  const $feedbackButton = $(options.feedbackButton);
  $feedbackButton.data('question', '');
  $feedbackButton.data('answer', '');
  $feedbackButton.data('position', -1);
}

function resetSuggestions($trigger, options) {
  $(options.suggestionsContainer).hide();
  const $suggestions = $(`${options.suggestionsContainer} ${options.chatButton}`);
  $suggestions.addClass(options.hideClass);

  // Mark clicked so it never reappears
  $(`${options.suggestionsContainer} ${options.chatButton}[data-rank='${$trigger.data('rank')}']`)
    .attr('data-clicked', '1');

  // Reveal next 5 suggestions if any
  let suggestionCount = 0;
  $suggestions.each(function() {
    const $suggestion = $(this);
    if (!$suggestion.attr('data-clicked') && suggestionCount < options.maxSuggestions) {
      $suggestion.removeClass(options.hideClass);
      suggestionCount++;
    }
  });
}

/**
 * Binds all event handlers
 */
function bindEventHandlers(options) {
  // Remove any previously bound events to prevent duplicates
  $(document).off('click.chatWindow', options.chatButton);
  $(document).off('click.chatWindow', options.feedbackButton);
  $(document).off('click.chatWindow', options.chatWindowClose);
  $(document).off('keydown.chatWindow');

  // Regular chat buttons (excluding "something else" buttons)
  $(document).on('click.chatWindow', options.chatButton, function () {
    if (!$(this).is(options.somethingElseButton)) {
      openChatWindow($(this), options);
    }
  });

  // Feedback button handling
  $(document).on('click.chatWindow', options.feedbackButton, function () {
    handleFeedbackButton($(this), options);
  });

  // Chat window close button
  $(document).on('click.chatWindow', options.chatWindowClose, function() {
    closeChatWindow(options);
  });
  
  // Handle escape key to close the chat window
  $(document).on('keydown.chatWindow', function(e) {
    if (e.key === 'Escape' && $(options.chatWindowTarget).hasClass(options.activeClass)) {
      closeChatWindow(options);
    }
  });
}

/**
 * Main plugin initialization function
 */
export default function chatWindow(el, userOptions = {}) {
  const $element = $(el);
  
  // Prevent multiple initializations
  if ($element.data(PLUGIN_NAME + 'Initialized')) {
    return $element;
  }
  
  // Merge default options with user options
  const options = mergeOptions(DEFAULTS, userOptions, $element);
  
  // Initialize a11y attributes
  initializeA11yAttributes(options);
  
  // Bind event handlers
  bindEventHandlers(options);
  
  // Mark as initialized
  $element.data(PLUGIN_NAME + 'Initialized', true);
  
  // Store options for later access
  $element.data(PLUGIN_NAME + 'Options', options);
  
  // Return element for chaining
  return $element;
}

/**
 * Sets up accessibility attributes on the chat window elements
 */
function initializeA11yAttributes(options) {
  // Set up header for accessibility
  const $header = $(options.windowHeader);
  if ($header.length) {
    // Ensure the header can receive focus
    if (!$header.attr('tabindex')) {
      $header.attr('tabindex', '-1');
    }
    
    // Add appropriate ARIA role and label if not present
    if (!$header.attr('role')) {
      $header.attr('role', 'dialog');
    }
    
    if (!$header.attr('aria-labelledby') && $header.find('h2, h3, h4').length) {
      const headingId = 'chat-window-heading';
      $header.find('h2, h3, h4').first().attr('id', headingId);
      $header.attr('aria-labelledby', headingId);
    } else if (!$header.attr('aria-label')) {
      $header.attr('aria-label', 'Chat Window');
    }
  }
  
  // Make close button accessible
  $(options.chatWindowClose).attr('aria-label', 'Close chat window');
  
  // Set appropriate ARIA attributes on the chat window
  $(options.chatWindowTarget).attr({
    'role': 'dialog',
    'aria-modal': 'true'
  });
}

// Register as jQuery plugin
registerJQueryPlugin(PLUGIN_NAME, chatWindow);


// WEBPACK FOOTER //
// ./src/scripts/entry-points/pdp/chat-window-qa.js